import * as React from "react";
import { useMutation, useQuery } from "convex/react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { api } from "@/lib/convexApi";
import { formatDate } from "@/lib/format";
import { CreditKeyConfirmDialog } from "./CreditKeyConfirmDialog";
import { CreditKeyPanelBoundary } from "./CreditKeyPanelBoundary";

type AccessAudit = { enabled: boolean; reason?: string; changedAt: number; actorName: string };
type PauseAudit = { paused: boolean; reason: string; changedAt: number; actorName: string };
type CustomerAccess = {
  enabled: boolean;
  paused: boolean;
  revision: number;
  setupStatus: "not_started" | "started" | "completed";
  audit: AccessAudit[];
};
type GlobalSettings = {
  paused: boolean;
  effectivePaused: boolean;
  environmentPaused: boolean;
  revision: number;
  audit: PauseAudit[];
};
type PendingChange =
  | { kind: "access"; enabled: boolean; expectedRevision: number }
  | { kind: "pause"; paused: boolean; expectedRevision: number };

export function CreditKeyAccessPanel({ customerId, showGlobalControls = true }: { customerId: string; showGlobalControls?: boolean }) {
  const identity = useAuthIdentity();
  // Do not mount privileged queries or even a locked-feature teaser for staff
  // who cannot manage the allowlist. The backend independently enforces this.
  if (identity.userRole !== "admin" || !identity.isAuthBridgeReady || !identity.isConvexAuthenticated) return null;
  return (
    <CreditKeyPanelBoundary key={customerId}>
      <AuthorizedCreditKeyAccessPanel customerId={customerId} showGlobalControls={showGlobalControls} />
    </CreditKeyPanelBoundary>
  );
}

function AuthorizedCreditKeyAccessPanel({ customerId, showGlobalControls }: { customerId: string; showGlobalControls: boolean }) {
  const access = useQuery(api.creditKey.getCustomerAccess, { customerId }) as CustomerAccess | undefined;
  const settings = useQuery(api.creditKey.getGlobalSettings, showGlobalControls ? {} : "skip") as GlobalSettings | undefined;
  const setAccess = useMutation(api.creditKey.setCustomerAccess);
  const setPaused = useMutation(api.creditKey.setGlobalPaused);
  const [pendingChange, setPendingChange] = React.useState<PendingChange | null>(null);
  const sectionId = React.useId();

  if (access === undefined || (showGlobalControls && settings === undefined)) {
    return <p role="status" className="text-sm text-muted-foreground">Loading Credit Key access…</p>;
  }

  const changeIsAccess = pendingChange?.kind === "access";
  const title = pendingChange?.kind === "access"
    ? (pendingChange.enabled ? "Enable Credit Key for this account?" : "Remove this account's Credit Key access?")
    : (pendingChange?.paused ? "Pause new Credit Key financing for everyone?" : "Resume new Credit Key financing for allowed accounts?");
  const confirmLabel = pendingChange?.kind === "access"
    ? (pendingChange.enabled ? "Enable account access" : "Remove account access")
    : (pendingChange?.paused ? "Pause new financing" : "Resume new financing");

  async function confirmChange(reason: string) {
    if (!pendingChange) throw new Error("No access change selected");
    try {
      if (pendingChange.kind === "access") {
        await setAccess({ customerId, enabled: pendingChange.enabled, expectedRevision: pendingChange.expectedRevision, reason: reason.trim() || undefined });
        toast.success(pendingChange.enabled ? "Account added to the Credit Key allowlist" : "Account removed from the Credit Key allowlist");
      } else {
        await setPaused({ paused: pendingChange.paused, expectedRevision: pendingChange.expectedRevision, reason });
        toast.success(pendingChange.paused ? "New Credit Key financing paused" : "Credit Key pause setting updated. Environment restrictions still apply.");
      }
    } catch {
      toast.error("Unable to confirm the access change. Review the current status before trying again.", { duration: Infinity });
      throw new Error("Credit Key access change not confirmed");
    }
  }

  return (
    <Card role="region" aria-labelledby={`${sectionId}-title`}>
      <CardHeader>
        <h3 id={`${sectionId}-title`} className="font-semibold">Credit Key access</h3>
        <CardDescription>
          Access is enabled separately for each customer or employee account. Credit Key approval and Anthem credit terms are managed separately.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 space-y-2">
            <Badge variant="outline">{access.enabled ? "On the allowlist" : "Hidden from this account"}</Badge>
            <p className="text-sm text-muted-foreground">
              {access.enabled
                ? "This account can see Credit Key when new financing is available. Financing approval is still handled by Credit Key."
                : "No Credit Key checkout option, setup card, or marketing is shown. Existing financed orders can still be viewed and serviced."}
            </p>
            {access.enabled ? (
              <p className="text-sm">{access.setupStatus === "completed" ? "Setup completed in Credit Key" : access.setupStatus === "started" ? "Setup in progress" : "Setup has not been started"}</p>
            ) : null}
            {access.enabled && access.paused ? <p className="text-sm text-muted-foreground">New financing is currently paused. Access will take effect when financing resumes.</p> : null}
          </div>
          <Button type="button" variant="outline" onClick={() => setPendingChange({ kind: "access", enabled: !access.enabled, expectedRevision: access.revision })}>
            {access.enabled ? "Remove account access" : "Enable account access"}
          </Button>
        </div>

        {showGlobalControls && settings ? <div className="space-y-3 rounded-lg bg-muted p-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1 space-y-2">
              <h4 className="text-sm font-medium">New financing across Anthem</h4>
              <p className="text-sm">{settings.effectivePaused ? "Paused for every account" : "Available to allowlisted accounts only"}</p>
              <p className="text-xs text-muted-foreground">
                {settings.environmentPaused
                  ? "An environment safety restriction is active. Changing the admin pause setting cannot enable financing while that restriction remains."
                  : "This switch affects new financing, not existing payment history, refunds, or fulfillment."}
              </p>
              <p className="text-xs text-muted-foreground">Admin pause setting: {settings.paused ? "On" : "Off"}</p>
            </div>
            <Button type="button" variant="outline" onClick={() => setPendingChange({ kind: "pause", paused: !settings.paused, expectedRevision: settings.revision })}>
              {settings.paused ? "Resume new financing" : "Pause new financing"}
            </Button>
          </div>
        </div> : null}

        <details className="space-y-3">
          <summary className="cursor-pointer text-sm font-medium">View account access history ({access.audit.length})</summary>
          {access.audit.length === 0 ? <p className="text-sm text-muted-foreground">No access changes. Account access is off by default.</p> : (
            <ol className="space-y-4 text-sm">
              {access.audit.map((entry, index) => (
                <li key={`${entry.changedAt}-${index}`} className="space-y-1">
                  <p className="font-medium">{entry.enabled ? "Access enabled" : "Access removed"} · {entry.actorName}</p>
                  {entry.reason ? <p className="break-words whitespace-pre-wrap">{entry.reason}</p> : null}
                  <p className="text-xs text-muted-foreground">{formatDate(entry.changedAt, { month: "short", hour: "numeric", minute: "2-digit" })}</p>
                </li>
              ))}
            </ol>
          )}
        </details>
        {showGlobalControls && settings ? <details className="space-y-3">
          <summary className="cursor-pointer text-sm font-medium">View global pause history ({settings.audit.length})</summary>
          {settings.audit.length === 0 ? <p className="text-sm text-muted-foreground">No pause changes. New financing is paused by default.</p> : (
            <ol className="space-y-4 text-sm">
              {settings.audit.map((entry, index) => (
                <li key={`${entry.changedAt}-${index}`} className="space-y-1">
                  <p className="font-medium">{entry.paused ? "New financing paused" : "Admin pause removed"} · {entry.actorName}</p>
                  <p className="break-words whitespace-pre-wrap">{entry.reason}</p>
                  <p className="text-xs text-muted-foreground">{formatDate(entry.changedAt, { month: "short", hour: "numeric", minute: "2-digit" })}</p>
                </li>
              ))}
            </ol>
          )}
        </details> : null}
      </CardContent>
      {pendingChange ? (
        <CreditKeyConfirmDialog
          title={title}
          confirmLabel={confirmLabel}
          requireReason={!changeIsAccess}
          optionalReason={changeIsAccess}
          destructive={pendingChange.kind === "access" ? !pendingChange.enabled : pendingChange.paused}
          description={changeIsAccess
            ? "This changes visibility and permission for this account only. It does not approve a credit line or change existing orders, refunds, or warehouse inventory."
            : "This changes new financing for every allowlisted account, including open checkouts. Existing orders and payment servicing remain available. Environment restrictions still apply."}
          failureMessage="Unable to confirm the change. Close this dialog and review the current status before trying again; another admin may have changed it."
          onConfirm={confirmChange}
          onClose={() => setPendingChange(null)}
        />
      ) : null}
    </Card>
  );
}
